import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';

import ActionButton from 'components/buttons/action-button/ActionButton';

import InsightsReportsQueriesModalCollabary from '../collabary/modals/InsightsReportsQueriesModalCollabary';

import { __t, applyTranslations } from 'utils/language';
import {
  allowNumbersOnly
} from 'modules/insights/reports/utils/insights-reports';
import {
  CHANNELS,
  HASHTAGS,
  IMPRESSIONS,
  KEYWORDS,
  KPI_DEFAULTS,
  REACH,
  STEPS,
  TITLE,
  getErrorStatus
} from 'modules/insights/reports/utils/queries/insights-reports-queries';

import type {
  Query,
  QueryChannel,
  QueryFieldType,
  QueryKeyword,
  QueryModalErrors,
  QueryModalTextFields,
  QuerySave,
  QueryStep
} from 'modules/insights/reports/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));

type Props = {
  query: Query,
  stepIndexStart: number | null,
  onSaveQuery: (data: QuerySave) => void,
  onCloseModal: () => void
};

type State = {
  stepIndex: number,
  stepCount: number,
  stepDirection: number,
  textFields: QueryModalTextFields,
  errors: QueryModalErrors,
  hasErrors: boolean,
  isShowingKpis: boolean
};

const STEP_DIRECTION = 'step-direction';
const COLLABARY_MSG_KEY = 'collabary_common';

class InsightsReportsQueriesModal extends React.PureComponent<Props, State> {
  state: State = {
    stepIndex: this.props.stepIndexStart || 0,
    stepCount: 0,
    stepDirection: 0,
    textFields: {
      title: '',
      hashtags: [],
      channels: [],
      impressions: KPI_DEFAULTS.impressions,
      reach: KPI_DEFAULTS.reach
    },
    hasErrors: false,
    errors: {
      title: false,
      hashtags: false,
      channels: false,
      impressions: false,
      reach: false
    },
    isShowingKpis: false
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      ...this.state,
      stepCount: this.getTotalStepCount()
    };
  }

  componentDidMount() {
    const { query } = this.props;

    if (!isEmpty(query)) {
      this.handlePopulateTextFields();
    }
  }

  getTotalStepCount = (): number => (
    STEPS.length
  );

  getStepInfo(): QueryStep {
    const { stepIndex } = this.state;

    return STEPS[stepIndex];
  }

  getShowBackButtonStatus(): boolean {
    return this.state.stepIndex > 0;
  }

  getShowCancelButtonStatus(): boolean {
    return this.state.stepIndex === 0;
  }

  generateErrors(): void {
    const { stepIndex } = this.state;

    this.setState(state => {
      const hasTitleError = stepIndex === 0 && getErrorStatus(state.textFields, TITLE);
      const hasHashtagsError = stepIndex === 0 && getErrorStatus(state.textFields, HASHTAGS);
      const hasChannelsError = stepIndex === 1 && getErrorStatus(state.textFields, CHANNELS);

      return {
        ...state,
        hasErrors: hasTitleError || hasHashtagsError || hasChannelsError,
        errors: {
          ...state.errors,
          title: hasTitleError,
          hashtags: hasHashtagsError,
          channels: hasChannelsError
        }
      };
    });
  }

  handlePopulateTextFields = (): void => {
    const { query } = this.props;
    const impressions = isNumber(query.estimatedImpressionsMultiplier) ? query.estimatedImpressionsMultiplier! * 100 : '';
    const reach = isNumber(query.estimatedImpressionsMultiplier) ? query.estimatedReachMultiplier! * 100 : '';

    this.handleTextFieldChange(TITLE, query.name || '');
    this.handleTextFieldChange(IMPRESSIONS, String(impressions));
    this.handleTextFieldChange(REACH, String(reach));
    this.handleHashtagsFieldChange(query[KEYWORDS] || []);
    this.handleChannelsFieldChange(query[CHANNELS] || []);

    if (impressions || reach) {
      this.handleShowKpisToggle();
    }
  };

  handleShowKpisToggle = (): void => {
    this.setState(state => ({
      isShowingKpis: !state.isShowingKpis
    }));
  };

  handleTextFieldChange = (
    fieldType: QueryFieldType,
    value: string
  ): void => {
    const updatedValue = fieldType === IMPRESSIONS || fieldType === REACH
      ? allowNumbersOnly(value.slice(0, 3))
      : value;

    this.setState(state => ({
      textFields: {
        ...state.textFields,
        [String(fieldType)]: updatedValue
      }
    }));
  };

  handleHashtagsFieldChange = (
    hashtags: Array<QueryKeyword>
  ): void => {
    this.setState(state => ({
      textFields: {
        ...state.textFields,
        hashtags
      }
    }));
  };

  handleChannelsFieldChange = (
    channels: Array<QueryChannel>
  ): void => {
    this.setState(state => ({
      textFields: {
        ...state.textFields,
        channels
      }
    }));
  };

  handleShowStep = (
    event: React.SyntheticEvent<HTMLElement>
  ): void => {
    const target = event.currentTarget;
    const stepDirection = Number(target.dataset.stepDirection) || 0;

    this.generateErrors();

    this.setState(state => {
      let stepIndex = state.stepIndex + stepDirection;

      // prevent next step if there are errors
      if (stepDirection !== -1 && state.hasErrors) {
        stepIndex = state.stepIndex;
      }

      return {
        ...state,
        stepIndex,
        stepDirection
      };
    });
  };

  handleSaveQuery = (): void => {
    const { query, onSaveQuery, onCloseModal } = this.props;
    const { textFields } = this.state;

    this.generateErrors();

    if (getErrorStatus(textFields, CHANNELS)) {
      return;
    }

    onSaveQuery({ query, textFields });
    onCloseModal();
  };

  renderCancelButton = (): React.ReactNode | undefined | null => {
    const { onCloseModal } = this.props;
    const showButton = this.getShowCancelButtonStatus();

    if (!showButton) {
      return null;
    }

    return (
      <ActionButton
        color="tertiary"
        onClick={onCloseModal}
      >
        {__t(`${COLLABARY_MSG_KEY}.action_button.cancel`)}
      </ActionButton>
    );
  };

  renderBackButton = (): React.ReactNode | undefined | null => {
    const showButton = this.getShowBackButtonStatus();

    if (!showButton) {
      return null;
    }

    return (
      <ActionButton
        color="tertiary"
        data={{ [STEP_DIRECTION]: -1 }}
        onClick={this.handleShowStep}
      >
        {__t(`${COLLABARY_MSG_KEY}.action_button.back`)}
      </ActionButton>
    );
  };

  renderActionButton = (): JSX.Element => {
    const { stepIndex } = this.state;
    const isLastSlide = stepIndex === this.getTotalStepCount() - 1;
    const buttonText = __t(`${COLLABARY_MSG_KEY}.action_button.${isLastSlide ? 'save' : 'next'}`);

    return (
      <ActionButton
        data={{ [STEP_DIRECTION]: 1 }}
        onClick={isLastSlide ? this.handleSaveQuery : this.handleShowStep}
      >
        {buttonText}
      </ActionButton>
    );
  };

  renderCollabary(): React.ReactNode {
    const { stepIndex, stepCount, textFields, errors, isShowingKpis } = this.state;

    return (
      <div className="insights-reports-queries-modal__collabary">
        <InsightsReportsQueriesModalCollabary
          stepIndex={stepIndex}
          stepCount={stepCount}
          stepInfo={this.getStepInfo()}
          textFields={textFields}
          errors={errors}
          isShowingKpis={isShowingKpis}
          onRenderActionButton={this.renderActionButton}
          onRenderCancelButton={this.renderCancelButton}
          onRenderBackButton={this.renderBackButton}
          onTextFieldChange={this.handleTextFieldChange}
          onHashtagsFieldChange={this.handleHashtagsFieldChange}
          onChannelsFieldChange={this.handleChannelsFieldChange}
          onShowKpisToggle={this.handleShowKpisToggle}
        />
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="insights-reports-queries-modal">
        {this.renderCollabary()}
      </div>
    );
  }
}

export default InsightsReportsQueriesModal;
